import React, { useState, useCallback, memo, useMemo } from "react";
import {
  Box,
  Typography,
  Radio,
  FormControlLabel,
  IconButton,
  FormControl,
  FormLabel,
  Button,
  Grid,
} from "@mui/material";
import { ReactComponent as IconClose } from "assets/svg/20-close.svg";
import CustomPopover from "components/common/CustomPopover";
import pxToRem from "theme/base/pxToRem";
import { ButtonTimeLate, ReasonTypeOption } from "../style";
import { useDispatch, useSelector } from "react-redux";
import {
  minDateSelector,
  selectArriveLateDetailData,
  selectFormReqArrivingLateLeaveEarly,
  selectListDataDetailArriveLate,
  selectListReasonArriveLate,
} from "modules/DeclareLeave/redux/selector";
import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import {
  formReqArrivingLateLeaveEarlyUpdate,
  TypeSelect,
} from "modules/DeclareLeave/redux/formReqArrivingLateLeaveEarly";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { timeReq, typeReasonColor } from "../const";
import { useLegacyEffect } from "helper/customHook";

export interface ArrivingLateLeavingEarlyProps {
  date: string;
  isOutsideCurrentMonth?: boolean;
  year?: number;
}

export enum ReqType {
  ArrivingLate = "LATE",
  LeavingEarly = "EARLY",
}

const ArrivingLateLeavingEarly = (props: ArrivingLateLeavingEarlyProps) => {
  const { data } = useSelector(selectFormReqArrivingLateLeaveEarly);
  const dataDetail = useSelector(selectArriveLateDetailData);
  const typeReason = useSelector(selectListReasonArriveLate);
  const minDate = useSelector(minDateSelector);
  const { minRegArriveLate } = minDate || {};

  const getYear = props?.year || moment(new Date()).year();
  const fullDate = `${props.date}/${getYear}`;

  const isIncludesDataDetail = (type, id) => {
    return dataDetail?.details?.some(
      (it) =>
        moment(it?.fromDate, "YYYY-MM-DD")?.toISOString() ===
          moment(fullDate, "DD/MM/YYYY")?.toISOString() &&
        type === it.type &&
        id === it?.id
    );
  };

  const isDisable = (dataIdx) => {
    if (!fullDate) return true; // Nếu ngày không hợp lệ thì disable
    const givenDate = moment(fullDate, "DD/MM/YYYY"); // Chuyển đổi ngày nhập vào moment

    if (!givenDate.isValid()) return true; // Nếu ngày không hợp lệ thì disable

    const dateNow = moment(
      minRegArriveLate ? minRegArriveLate : new Date(),
      "YYYY/MM/DD"
    ).format("DD/MM/YYYY"); // Định dạng kết quả // Ngày trước đó 24 tháng

    if (givenDate.isBefore(moment(dateNow, "DD/MM/YYYY"))) return true; // Nếu ngày nhập trước ngày 24 tháng trước => disabled
    if (props?.isOutsideCurrentMonth) return true;
    if (data?.type === TypeSelect.LONG_TERM) return true; // nếu là loại chọn nhiều ngày thì return true
    if (dataDetail && dataDetail?.details && dataDetail?.details?.length) {
      return !isIncludesDataDetail(dataIdx?.type, dataIdx?.id); // nếu là màn hình chỉnh sửa và có tồn tại đơn trong ngày đó return false
    } else {
      return !!dataIdx?.reasonCode && !dataIdx?.isNew; // nếu đơn trong ngày trùng với kiểu => true vd: đã tồn tại dơn đi sớm => disabled đơn đi sớm
    }
  };

  const colorIndex = (dataIdx): number => {
    const colorIndx = typeReason?.findIndex(
      (it) => it.value === dataIdx?.reasonCode
    );

    return dataIdx && typeReasonColor.length - 1 < colorIndx ? 1 : colorIndx; // fix mặc định màu khi không tồn tại trong mã màu
  };

  const getColor = (disabled: boolean, dataIdx: any, isIncludes: boolean) => {
    if (
      !disabled ||
      (dataIdx?.type === TypeSelect.LONG_TERM && (isIncludes || dataIdx?.isNew))
    ) {
      return "#fff !important";
    }
    return "#B3B3B3";
  };

  const getBackgroundColor = (
    disabled: boolean,
    dataIndx: any,
    color: string,
    isIncludes: boolean
  ) => {
    if (
      !disabled ||
      (dataIndx?.type === TypeSelect.LONG_TERM &&
        (isIncludes || dataIndx?.isNew))
    )
      return color || "#E6E6E6";
    return "#E6E6E6";
  };

  const getLabel = (data: any) => {
    const label = data?.type === ReqType.ArrivingLate ? "Đi muộn" : "Về sớm";

    if (data?.reasonCode) {
      const fullLabel = `${label}: ${data?.minuteAmount} phút`;
      return (
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          width={"100%"}
        >
          <Typography
            sx={{
              fontSize: { xs: pxToRem(10), lg: pxToRem(12) },
              color: getColor(
                isDisable(data),
                data,
                isIncludesDataDetail(data?.type, data?.id)
              ),
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: { md: pxToRem(50), lg: pxToRem(100) },
            }}
          >
            {fullLabel}
          </Typography>
          <KeyboardArrowDownIcon
            fontSize="large"
            sx={{
              color: getColor(
                isDisable(data),
                data,
                isIncludesDataDetail(data?.type, data?.id)
              ),
            }}
          />
        </Box>
      );
    } else {
      return (
        <>
          <AddIcon
            fontSize="large"
            sx={{ marginRight: "5px", color: "#182BCF" }}
          />
          {label}
        </>
      );
    }
  };

  const listOrder = (listData: any[], type: ReqType) => {
    return listData?.filter(
      (it) =>
        it.fromDate?.toISOString() ===
          moment(fullDate, "DD/MM/YYYY")?.toISOString() && type === it?.type
    );
  };

  const useRequestList = (type: ReqType, date: string, deps: any[]) => {
    return useMemo(() => {
      const listDetails = listOrder(data?.details, type);
      const defaultData = {
        type,
        date,
        isDisableDetail: true,
      };

      if (!listDetails.length) return [defaultData];

      if (listDetails.length === 1) {
        if (listDetails[0]?.allowDuplicate) {
          return [...listDetails, defaultData];
        }
        return listDetails;
      }

      return listDetails;
    }, deps);
  };

  const listArriveLate = useRequestList(ReqType.ArrivingLate, props.date, [
    fullDate,
    data?.details,
  ]);

  const listLeaveEarly = useRequestList(ReqType.LeavingEarly, props.date, [
    fullDate,
    data?.details,
  ]);

  return (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      flexDirection={"column"}
      gap={2}
      width={"100%"}
      alignItems={"center"}
    >
      {listArriveLate?.map((it, idx) => {
        return (
          <CustomPopover
            props={{ ...props, type: it.type, id: it?.id, idx }}
            buttonLabel={getLabel(it)}
            styleButton={{
              backgroundColor: getBackgroundColor(
                isDisable(it),
                it,
                `${typeReasonColor[colorIndex(it)]} !important`,
                isIncludesDataDetail(it.type, it?.id)
              ),
              border: "none",
              "&:hover": {
                backgroundColor: `#F2F2F2 `,
                border: "solid 2px #27272cff",
              },
            }}
            disabled={isDisable(it)}
          >
            {({ props, onClose }) => (
              <PopupContent props={{ ...props }} onClose={onClose} />
            )}
          </CustomPopover>
        );
      })}
      {listLeaveEarly?.map((it, idx) => {
        return (
          <CustomPopover
            props={{ ...props, type: it.type, id: it?.id, idx }}
            buttonLabel={getLabel(it)}
            styleButton={{
              backgroundColor: getBackgroundColor(
                isDisable(it),
                it,
                `${typeReasonColor[colorIndex(it)]} !important`,
                isIncludesDataDetail(it.type, it?.id)
              ),
              border: "none",
              "&:hover": {
                backgroundColor: `#F2F2F2 `,
                border: "solid 2px #D1D5F5",
              },
            }}
            disabled={isDisable(it)}
          >
            {({ props, onClose }) => (
              <PopupContent props={{ ...props }} onClose={onClose} />
            )}
          </CustomPopover>
        );
      })}
      {/* 
      <CustomPopover
        props={{ ...props, type: ReqType.ArrivingLate }}
        buttonLabel={getLabel(ReqType.ArrivingLate)}
        styleButton={{
          backgroundColor: getBackgroundColor(
            isDisable(ReqType.ArrivingLate),
            data?.type,
            `${typeReasonColor[colorIndex(ReqType.ArrivingLate)]} !important`,
            isIncludesDataDetail(ReqType.ArrivingLate),
            ReqType.ArrivingLate
          ),
          border: "none",
          "&:hover": {
            backgroundColor: `#F2F2F2 `,
            border: "solid 2px #D1D5F5",
          },
        }}
        disabled={isDisable(ReqType.ArrivingLate)}
      >
        {({ props, onClose }) => (
          <PopupContent props={{ ...props }} onClose={onClose} />
        )}
      </CustomPopover>
      <CustomPopover
        props={{ ...props, type: ReqType.LeavingEarly }}
        buttonLabel={getLabel(ReqType.LeavingEarly)}
        styleButton={{
          backgroundColor: getBackgroundColor(
            isDisable(ReqType.LeavingEarly),
            data?.type,
            `${typeReasonColor[colorIndex(ReqType.LeavingEarly)]} !important`,
            isIncludesDataDetail(ReqType.LeavingEarly),
            ReqType.LeavingEarly
          ),
          border: "none",
          "&:hover": {
            backgroundColor: `#F2F2F2 `,
            border: "solid 2px #D1D5F5",
          },
        }}
        disabled={isDisable(ReqType.LeavingEarly)}
      >
        {({ props, onClose }) => (
          <PopupContent props={{ ...props }} onClose={onClose} />
        )}
      </CustomPopover> */}
    </Box>
  );
};

const PopupContent = memo(
  ({
    props,
    onClose,
  }: {
    props: ArrivingLateLeavingEarlyProps & {
      type: ReqType;
      idx: number;
      id?: string;
    };
    onClose: () => void;
  }) => {
    const { type } = props;
    const getYear = props?.year || moment(new Date()).year();
    const fullDate = `${props.date}/${getYear}`;
    const dispatch = useDispatch();
    const dataDetails = useSelector(selectListDataDetailArriveLate);
    const typeReason = useSelector(selectListReasonArriveLate);

    const [reqData, setReqData] = useState({
      time: "",
      reason: "",
    });

    const dataDate = useMemo(() => {
      return dataDetails?.find(
        (it) =>
          it.fromDate?.toISOString() ===
            moment(fullDate, "DD/MM/YYYY")?.toISOString() &&
          type === it?.type &&
          it?.id === props.id
      );
    }, [dataDetails, type, fullDate, props.id]);

    const totalTimeInDate = useMemo(() => {
      return dataDetails?.find(
        (it) =>
          it.fromDate?.toISOString() ===
            moment(fullDate, "DD/MM/YYYY")?.toISOString() &&
          type === it?.type &&
          it?.id !== props.id
      )?.minuteAmount;
    }, [dataDetails]);

    useLegacyEffect(() => {
      if (!dataDate) return;
      setReqData((prev) => ({
        ...prev,
        time: dataDate?.minuteAmount
          ? String(dataDate?.minuteAmount)
          : prev?.time,
        reason: dataDate?.reasonCode || prev?.reason,
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataDate, props.date]);

    const handleClose = () => {
      setReqData({
        time: "",
        reason: "",
      });
      onClose();
    };

    const handleConfirm = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        const fullDate = `${props.date}/${getYear}`;
        const dateIdx = dataDetails?.findIndex(
          (it) =>
            it.fromDate?.toISOString() ===
              moment(fullDate, "DD/MM/YYYY")?.toISOString() &&
            type === it?.type &&
            !!props.id &&
            it.id === props.id
        );

        const dataDate = {
          minuteAmount: reqData?.time,
          reasonCode: reqData?.reason,
        };

        if (dateIdx > -1) {
          dispatch(
            formReqArrivingLateLeaveEarlyUpdate({
              details: dataDetails?.map((it, idx) =>
                idx === dateIdx
                  ? {
                      ...it,
                      ...dataDate,
                      isNew: true,
                      type,
                    }
                  : it
              ),
            })
          );
        } else {
          dispatch(
            formReqArrivingLateLeaveEarlyUpdate({
              details: [
                ...dataDetails,
                {
                  id: dataDetails[dateIdx]!?.id || Math.random().toString(36),
                  toDate: moment(fullDate, "DD/MM/YYYY"),
                  fromDate: moment(fullDate, "DD/MM/YYYY"),
                  ...dataDate,
                  isNew: true,
                  type,
                },
              ],
            })
          );
        }
        // Your confirm logic here
        handleClose();
      },
      [dataDetails, reqData, props.date, type, dispatch]
    );

    const handleTypeChange = useCallback((e) => {
      setReqData((prev) => ({ ...prev, reason: e.target.value }));
    }, []);

    return (
      <Box sx={{ padding: "16px" }}>
        <Box display={"flex"} justifyContent={"space-between"}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            textAlign="center"
            gutterBottom
          >
            Ngày {props.date} -{" "}
            {type === ReqType.ArrivingLate ? "Đi muộn" : "Về sớm"}
          </Typography>
          <IconButton onClick={handleClose}>
            <IconClose />
          </IconButton>
        </Box>
        <Box width={pxToRem(320)}>
          <Typography sx={{ marginBottom: "12px" }}>Chọn thời gian</Typography>
          <Grid container gap={1} justifyContent="center" alignItems={"center"}>
            {timeReq.map((time, indx) => (
              <Grid item xs={3} md={3.5} key={indx}>
                <ButtonTimeLate
                  onClick={() => {
                    setReqData({ ...reqData, time: time.value });
                  }}
                  variant={
                    reqData.time === time.value ? "contained" : "outlined"
                  }
                  selected={reqData.time === time.value}
                  disabled={Number(totalTimeInDate) + Number(time?.value) > 120} // Assuming 120 is the max minutes allowed in a day
                >
                  {time.label}
                </ButtonTimeLate>
              </Grid>
            ))}
          </Grid>

          {/* Leave Type Selection */}
          <FormControl component="fieldset" fullWidth margin="normal">
            <FormLabel
              sx={(theme) => ({
                marginBottom: pxToRem(16),
                color: theme.palette.text.textPrimary,
              })}
            >
              Chọn lý do
            </FormLabel>
            <ReasonTypeOption
              value={reqData?.reason}
              onChange={handleTypeChange}
            >
              {typeReason?.map((type, idx) => (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={"start"}
                  gap={1}
                  key={type.value}
                  sx={{ marginBottom: "8px", backgroundColor: "#F2F2F2" }}
                >
                  <Box
                    sx={{
                      width: pxToRem(5),
                      height: "100%",
                      borderTopLeftRadius: pxToRem(4),
                      borderBottomLeftRadius: pxToRem(4),
                      background:
                        typeReasonColor[
                          idx > typeReasonColor.length - 1 ? 1 : idx
                        ],
                    }}
                  ></Box>

                  <FormControlLabel
                    key={type.value}
                    value={type.value}
                    control={<Radio />}
                    label={type.label}
                    sx={(theme) => ({
                      ".Mui-checked": {
                        color: `${theme.palette.colorsBase.blue_0} !important`, // Label color when checked
                      },
                      span: {
                        fontSize: pxToRem(12),
                      },
                    })}
                  />
                </Box>
              ))}
            </ReasonTypeOption>
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ minWidth: 120 }}
            onClick={handleConfirm}
            disabled={!reqData?.reason || !reqData.time}
          >
            Xác nhận
          </Button>
        </Box>
      </Box>
    );
  }
);

export default ArrivingLateLeavingEarly;
